import React, { useState, useMemo, useCallback } from 'react';
import WorkspaceContext from './WorkspaceContext';
import {
  getWorkspaces,
  getWorkspace,
  createWorkspace as apiCreateWorkspace,
  updateWorkspace as apiUpdateWorkspace,
  deleteWorkspace as apiDeleteWorkspace,
} from '../api/workspaceApi';

/**
 * WorkspaceProvider manages state and operations related to user career workspaces.
 * It provides activeWorkspace metadata, list of workspaces, loading, and error states.
 */
export const WorkspaceProvider = ({ children }) => {
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Loads all workspaces for the authenticated user and stores them in state.
   */
  const loadWorkspaces = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getWorkspaces();
      setWorkspaces(data || []);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load workspaces');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Loads the details of a single workspace and updates activeWorkspace.
   * @param {string} id - The workspace ID.
   */
  const loadWorkspace = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getWorkspace(id);
      const ws = data?.workspace || data;
      setActiveWorkspace(ws);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to load workspace');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Creates a new workspace and updates the workspaces list.
   * @param {Object} workspaceData - Configuration data for the new workspace.
   */
  const createWorkspace = useCallback(async (workspaceData) => {
    setIsLoading(true);
    setError(null);
    try {
      const newWs = await apiCreateWorkspace(workspaceData);
      setWorkspaces((prev) => [...prev, newWs]);
      return newWs;
    } catch (err) {
      setError(err.message || 'Failed to create workspace');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Updates an existing workspace context and updates state lists.
   * @param {string} id - The workspace ID to update.
   * @param {Object} workspaceData - The fields to update.
   */
  const updateWorkspace = useCallback(async (id, workspaceData) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedWs = await apiUpdateWorkspace(id, workspaceData);
      setWorkspaces((prev) =>
        prev.map((w) => (w.id === id ? { ...w, ...updatedWs } : w))
      );
      setActiveWorkspace((prev) =>
        prev && prev.id === id ? { ...prev, ...updatedWs } : prev
      );
      return updatedWs;
    } catch (err) {
      setError(err.message || 'Failed to update workspace');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Deletes a workspace context and updates states.
   * @param {string} id - The workspace ID to delete.
   */
  const deleteWorkspace = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);
    try {
      await apiDeleteWorkspace(id);
      setWorkspaces((prev) => prev.filter((w) => w.id !== id));
      setActiveWorkspace((prev) => (prev && prev.id === id ? null : prev));
    } catch (err) {
      setError(err.message || 'Failed to delete workspace');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value = useMemo(() => ({
    activeWorkspace,
    setActiveWorkspace,
    workspaces,
    setWorkspaces,
    isLoading,
    setIsLoading,
    error,
    setError,
    loadWorkspaces,
    loadWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace
  }), [
    activeWorkspace,
    workspaces,
    isLoading,
    error,
    loadWorkspaces,
    loadWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace
  ]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceProvider;
